import * as PopoverPrimitive from "@radix-ui/react-popover";
import Icon from "~/components/shared/Icon";

interface RepositoryFetchWarningProps {
  type: "slow" | "timeout";
  onSwitchToManual: () => void;
  open: boolean;
  anchorRef: React.RefObject<HTMLElement>;
}

const MESSAGES = {
  slow: {
    text: "Loading repositories is taking longer than expected - this can happen with large numbers of repositories. You can enter your repository URL manually if you'd like to skip the wait.",
    action: "Enter URL manually",
  },
  timeout: {
    text: "We were unable to load your repository list - this can happen with large numbers of repositories. You can refresh the page to try again or enter your repository URL manually.",
    action: "Enter URL manually",
  },
};

/**
 * Warning popover for slow or failed repository fetching.
 *
 * This is a temporary stopgap solution to help users when repository fetching is slow or
 * times out. The entire component can be deleted once we've optimised repository loading
 * and server side searching.
 *
 * @param type - Type of warning to display ("slow" or "timeout")
 * @param onSwitchToManual - Callback when user clicks the action button
 * @param open - Whether the popover is open
 * @param anchorRef - Reference to the anchor element
 */
export function RepositoryFetchWarning({
  type,
  onSwitchToManual,
  open,
  anchorRef,
}: RepositoryFetchWarningProps) {
  const message = MESSAGES[type];

  return (
    <PopoverPrimitive.Root open={open}>
      <PopoverPrimitive.Anchor virtualRef={anchorRef} />
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          className="shadow-depth-800 no-title-tooltip z-50 w-[var(--radix-popover-trigger-width)] rounded-md border border-blue-200 bg-blue-50 outline-none"
          side="bottom"
          sideOffset={4}
          align="start"
          collisionPadding={8}
          onOpenAutoFocus={(event) => event.preventDefault()}
          aria-label="Repository loading warning"
        >
          <div className="p-3">
            <div className="flex gap-2">
              <div className="shrink-0">
                <Icon icon="info-circle" className="text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="m-0 text-sm text-blue-900">{message.text}</p>
                <button
                  type="button"
                  onClick={onSwitchToManual}
                  className="mt-2 inline-flex items-center rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  {message.action}
                </button>
              </div>
            </div>
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
